import { Badge, TabList, TabTrigger } from '@/components/ui';
import type { MetricViewActiveTab } from './MetricViewHeader';

/**
 * Horizontal scroll wrapper enables the tab row to remain usable at narrow
 * widths (phone/tablet) without truncating labels. `whitespace-nowrap` on
 * the row forces tabs to stay inline; `overflow-x-auto` lets the user drag
 * them horizontally. Above md, the full row fits and scrolling is inert.
 *
 * Drill-down (PRODOPSAI-385): when `activeTab` is one of the three
 * `hygiene-drill-*` values, an extra TabTrigger appears between Product
 * Flow and Engineering (DORA) labelled with the active drill-down's name
 * and a × close button. The close handler clears the URL `?tab` param
 * (effectively returning to Product Flow) — that's owned by the parent.
 */
const DRILL_LABELS: Record<string, string> = {
  'hygiene-drill-unlinked': 'Unlinked items',
  'hygiene-drill-statuses': 'Non-spine statuses',
  'hygiene-drill-types': 'Non-canonical types',
};

interface MetricViewTabsProps {
  activeTab: MetricViewActiveTab;
  onCloseDrill: () => void;
}

export function MetricViewTabs({ activeTab, onCloseDrill }: Readonly<MetricViewTabsProps>) {
  const isDrill = activeTab.startsWith('hygiene-drill-');
  const drillLabel = isDrill ? DRILL_LABELS[activeTab] : null;

  return (
    <div className="-mx-1 overflow-x-auto px-1 [scrollbar-width:thin]">
      <TabList className="w-max flex-nowrap whitespace-nowrap">
        <TabTrigger value="product-flow" className="shrink-0">◉ Product Flow</TabTrigger>
        {isDrill && drillLabel && (
          <span className="inline-flex items-center gap-1 border-b-2 border-primary -mb-px shrink-0">
            <TabTrigger value={activeTab} className="shrink-0 border-b-0 -mb-0">
              {drillLabel}
            </TabTrigger>
            <button
              type="button"
              onClick={onCloseDrill}
              aria-label={`Close ${drillLabel} tab`}
              className="px-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              ×
            </button>
          </span>
        )}
        <TabTrigger value="dora" className="shrink-0">⚡ Engineering (DORA)</TabTrigger>
        <TabTrigger value="code-quality" className="shrink-0">{'<>'} Code Quality</TabTrigger>
        <TabTrigger value="product-analytics" disabled className="shrink-0">▥ Product Analytics</TabTrigger>
        <TabTrigger value="business-kpis" disabled className="shrink-0">
          ◎ Business KPIs
          <Badge variant="success" className="ml-1 text-[9px]">NEW</Badge>
        </TabTrigger>
        <TabTrigger value="devex-survey" className="shrink-0">☺ DevEx Survey</TabTrigger>
      </TabList>
    </div>
  );
}
